import React, { useEffect, useState, useRef, useMemo } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Send,
  Sparkles,
  FileUp,
  Loader2,
  Pause,
  Play,
  Trash2,
  Users,
  Mail,
  Clock,
  X,
  CheckCircle2,
  AlertCircle,
  Ban,
  Copy,
} from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/errors";
import { useAgency } from "@/context/AgencyContext";
import { useAuth } from "@/context/AuthContext";

const STATUS_STYLES = {
  draft:      { label: "Draft",      cls: "bg-slate-100 text-slate-700 border-slate-200" },
  scheduled:  { label: "Scheduled",  cls: "bg-sky-50 text-sky-700 border-sky-200" },
  sending:    { label: "Sending…",   cls: "bg-amber-50 text-amber-700 border-amber-200" },
  sent:       { label: "Sent",       cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  paused:     { label: "Paused",     cls: "bg-orange-50 text-orange-700 border-orange-200" },
  canceled:   { label: "Canceled",   cls: "bg-slate-100 text-slate-500 border-slate-200" },
};

function StatusBadge({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.draft;
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-mono uppercase tracking-wider ${s.cls}`}
      data-testid={`campaign-status-${status}`}
    >
      {s.label}
    </span>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

export default function MarketingPage() {
  const { user } = useAuth();
  const { activeId: superAgencyId, active: superAgency } = useAgency();
  const isAdmin = user?.role === "admin";
  const activeAgencyId = isAdmin ? superAgencyId : user?.agency_id;
  const activeAgencyName = isAdmin ? superAgency?.name : user?.agency_name;

  const [campaigns, setCampaigns] = useState([]);
  const [agencyMeta, setAgencyMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [composerOpen, setComposerOpen] = useState(false);
  const [detailCampaign, setDetailCampaign] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [duplicateSource, setDuplicateSource] = useState(null);
  const [showUnsubs, setShowUnsubs] = useState(false);
  const [unsubs, setUnsubs] = useState(null);
  const [unsubsLoading, setUnsubsLoading] = useState(false);

  const [showBounces, setShowBounces] = useState(false);
  const [bounces, setBounces] = useState(null);
  const [bouncesLoading, setBouncesLoading] = useState(false);

  const loadUnsubs = async () => {
    if (!activeAgencyId) return;
    setUnsubsLoading(true);
    try {
      const { data } = await api.get("/marketing/unsubscribes/list", {
        params: isAdmin ? { agency_id: activeAgencyId } : {},
      });
      setUnsubs(data || []);
    } catch (e) {
      toast.error(formatApiError(e));
      setUnsubs([]);
    } finally {
      setUnsubsLoading(false);
    }
  };

  const loadBounces = async () => {
    if (!activeAgencyId) return;
    setBouncesLoading(true);
    try {
      const { data } = await api.get("/marketing/bounces/list", {
        params: isAdmin ? { agency_id: activeAgencyId } : {},
      });
      setBounces(data || []);
    } catch (e) {
      toast.error(formatApiError(e));
      setBounces([]);
    } finally {
      setBouncesLoading(false);
    }
  };

  const exportUnsubsCsv = () => {
    if (!unsubs || unsubs.length === 0) return;
    const rows = [["email", "unsubscribed_at", "reason"]];
    unsubs.forEach((u) => rows.push([u.email, u.unsubscribed_at || "", u.reason || ""]));
    downloadCsv(rows, `unsubscribes-${new Date().toISOString().slice(0, 10)}.csv`);
  };

  const exportBouncesCsv = () => {
    if (!bounces || bounces.length === 0) return;
    const rows = [["email", "name", "bounce_count", "last_bounced_at", "last_error"]];
    bounces.forEach((b) =>
      rows.push([b.email, b.name || "", b.bounce_count, b.last_bounced_at || "", b.last_error || ""])
    );
    downloadCsv(rows, `bounced-emails-${new Date().toISOString().slice(0, 10)}.csv`);
  };

  const downloadCsv = (rows, filename) => {
    const csv = rows
      .map((r) => r.map((c) => `"${String(c).replaceAll('"', '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const loadCampaigns = async () => {
    try {
      setLoading(true);
      const [{ data: list }, agencyRes] = await Promise.all([
        api.get("/marketing", {
          params: isAdmin && activeAgencyId ? { agency_id: activeAgencyId } : {},
        }),
        activeAgencyId
          ? api.get(`/rep-agencies/${activeAgencyId}`).catch(() => ({ data: null }))
          : Promise.resolve({ data: null }),
      ]);
      setCampaigns(list || []);
      setAgencyMeta(agencyRes.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCampaigns();
    // Poll while any campaign is sending so live counters update — BUT pause
    // the poll while the composer dialog is open so re-renders of the parent
    // never race with the user typing / picking a file.
    if (composerOpen) return;
    const interval = setInterval(() => {
      loadCampaigns();
    }, 6000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAgencyId, composerOpen]);

  const handleSend = async (c) => {
    try {
      const { data } = await api.post(`/marketing/${c.id}/send`);
      toast.success(`Sending to ${data.queued} recipients — paced at 1 every 3 seconds`);
      loadCampaigns();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handlePause = async (c) => {
    try {
      await api.post(`/marketing/${c.id}/pause`);
      toast.info("Campaign will pause after the current send");
      loadCampaigns();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handleDuplicate = async (c) => {
    try {
      const { data } = await api.post(`/marketing/${c.id}/duplicate`);
      toast.success("Duplicated — upload your new recipient list");
      loadCampaigns();
      setDuplicateSource(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/marketing/${confirmDelete.id}`);
      toast.success("Campaign deleted");
      setConfirmDelete(null);
      loadCampaigns();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!activeAgencyId && isAdmin) {
    return (
      <div className="p-8 max-w-4xl">
        <h1 className="text-3xl font-bold tracking-tight text-slate-900">Marketing</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Pick a rep agency from the top-bar switcher to view or create marketing campaigns.
        </p>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.14em] text-slate-500 font-mono">
            {activeAgencyName || "Marketing"}
          </div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 mt-1">
            Marketing campaigns
          </h1>
          <p className="text-sm text-slate-500 mt-1 max-w-xl">
            Draft AI-generated marketing emails from a prompt (and an optional spec sheet), upload a CSV, and send —
            paced at one email every 3 seconds with a self-serve unsubscribe.
          </p>
        </div>
        <Button
          onClick={() => setComposerOpen(true)}
          className="self-start md:self-auto"
          data-testid="marketing-new-campaign-btn"
        >
          <Sparkles className="w-4 h-4 mr-2" />
          New campaign
        </Button>
      </div>

      {agencyMeta && !agencyMeta.marketing_reply_to && (
        <div
          className="rounded-md border border-amber-200 bg-amber-50 p-3 text-[13px] text-amber-900 flex items-start gap-3"
          data-testid="marketing-reply-to-warning"
        >
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          <div className="flex-1 leading-relaxed">
            <strong>Heads up:</strong> No reply-to address is set for this agency yet.
            When a distributor hits <em>Reply</em> on a marketing email, their message
            goes to <code className="font-mono">noreply@myrepagency.com</code> — a
            black hole. Set one on the{" "}
            <a
              href="/agencies"
              className="underline font-semibold hover:text-amber-950"
            >
              Agencies page
            </a>{" "}
            (edit the agency → <em>Marketing reply-to</em>).
          </div>
        </div>
      )}

      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
          <div className="text-[11px] uppercase tracking-[0.12em] font-mono text-slate-500">
            {campaigns.length} campaign{campaigns.length === 1 ? "" : "s"}
          </div>
        </div>
        {loading ? (
          <div className="p-10 text-center text-sm text-slate-500 flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : campaigns.length === 0 ? (
          <div className="p-12 text-center">
            <Mail className="w-8 h-8 text-slate-300 mx-auto mb-3" />
            <div className="text-sm text-slate-600">No campaigns yet.</div>
            <div className="text-xs text-slate-400 mt-1">
              Click <span className="font-semibold">New campaign</span> to draft your first email with AI.
            </div>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100" data-testid="marketing-campaigns-list">
            {campaigns.map((c) => (
              <li
                key={c.id}
                className="p-4 hover:bg-slate-50 transition flex flex-col md:flex-row md:items-center gap-3"
                data-testid={`campaign-row-${c.id}`}
              >
                <button
                  className="flex-1 min-w-0 text-left"
                  onClick={() => setDetailCampaign(c)}
                  data-testid={`campaign-open-${c.id}`}
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <StatusBadge status={c.status} />
                    <div className="font-semibold text-slate-900 truncate">{c.subject}</div>
                  </div>
                  <div className="mt-1 text-xs text-slate-500 flex items-center gap-3 flex-wrap">
                    <span className="inline-flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      {c.sent_count}/{c.recipient_count} sent
                      {c.failed_count ? ` · ${c.failed_count} failed` : ""}
                      {c.skipped_unsub_count ? ` · ${c.skipped_unsub_count} unsub` : ""}
                    </span>
                    {c.sent_count > 0 && (
                      <span className="inline-flex items-center gap-2 font-mono">
                        <span className="text-sky-700">
                          {Math.round(((c.opened_count || 0) / c.sent_count) * 100)}% opens
                        </span>
                        <span className="text-slate-300">·</span>
                        <span className="text-sky-700">
                          {Math.round(((c.clicked_count || 0) / c.sent_count) * 100)}% clicks
                        </span>
                      </span>
                    )}
                    {c.scheduled_at && (
                      <span className="inline-flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {formatDate(c.scheduled_at)}
                      </span>
                    )}
                    {c.has_spec_sheet && (
                      <span
                        className="inline-flex items-center gap-1 text-slate-600"
                        title={c.spec_sheet_filename}
                      >
                        <FileUp className="w-3 h-3" />
                        {c.spec_sheet_filename || "PDF attached"}
                      </span>
                    )}
                    <span className="font-mono">{formatDate(c.created_at)}</span>
                  </div>
                </button>
                <div className="flex items-center gap-2 shrink-0">
                  {(c.status === "draft" || c.status === "paused" || c.status === "scheduled") && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleSend(c)}
                      data-testid={`campaign-send-${c.id}`}
                    >
                      <Play className="w-4 h-4 mr-1" />
                      {c.status === "paused" ? "Resume" : c.status === "scheduled" ? "Send now" : "Send"}
                    </Button>
                  )}
                  {c.status === "sending" && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handlePause(c)}
                      data-testid={`campaign-pause-${c.id}`}
                    >
                      <Pause className="w-4 h-4 mr-1" /> Pause
                    </Button>
                  )}
                  {c.status !== "sending" && (
                    <>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => handleDuplicate(c)}
                        title="Duplicate — reuse for a new recipient batch"
                        data-testid={`campaign-duplicate-${c.id}`}
                      >
                        <Copy className="w-4 h-4 text-slate-400 hover:text-sky-600" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => setConfirmDelete(c)}
                        title="Delete"
                        data-testid={`campaign-delete-${c.id}`}
                      >
                        <Trash2 className="w-4 h-4 text-slate-400 hover:text-red-500" />
                      </Button>
                    </>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <ComposerDialog
        open={composerOpen}
        onOpenChange={setComposerOpen}
        agencyId={activeAgencyId}
        agencyMeta={agencyMeta}
        onCreated={() => {
          setComposerOpen(false);
          loadCampaigns();
        }}
      />

      {/* --- Bounced emails viewer ---------------------------------------- */}
      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        <button
          type="button"
          onClick={() => {
            const next = !showBounces;
            setShowBounces(next);
            if (next && !bounces) loadBounces();
          }}
          className="w-full px-4 py-3 border-b border-slate-100 bg-slate-50 hover:bg-slate-100 flex items-center justify-between text-left transition"
          data-testid="marketing-toggle-bounces"
        >
          <div className="text-[11px] uppercase tracking-[0.12em] font-mono text-slate-600">
            Bounced emails {bounces !== null && <span className="text-slate-900 ml-1">· {bounces.length}</span>}
          </div>
          <span className="text-xs text-slate-400 font-mono">{showBounces ? "hide ▲" : "show ▼"}</span>
        </button>
        {showBounces && (
          <div>
            {bouncesLoading ? (
              <div className="p-6 text-center text-sm text-slate-500 flex items-center justify-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading…
              </div>
            ) : bounces && bounces.length === 0 ? (
              <div className="p-8 text-center text-sm text-slate-500">
                <AlertCircle className="w-6 h-6 text-slate-300 mx-auto mb-2" />
                No bounces recorded yet.
                <div className="text-[11px] text-slate-400 mt-1">
                  Bounces are populated by the Resend webhook once open/click tracking is fully wired up.
                </div>
              </div>
            ) : bounces && bounces.length > 0 ? (
              <>
                <div className="px-4 py-2 border-b border-slate-100 flex items-center justify-between text-xs gap-3">
                  <div className="text-slate-500 leading-relaxed">
                    Hard-bounced emails from past sends. Export this list and delete these rows from your
                    master CSV so future campaigns don&apos;t include them.
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={exportBouncesCsv}
                    data-testid="marketing-bounces-export-btn"
                    className="shrink-0"
                  >
                    Export CSV
                  </Button>
                </div>
                <ul className="divide-y divide-slate-100 max-h-80 overflow-y-auto">
                  {bounces.map((b) => (
                    <li key={b.email} className="px-4 py-2 text-sm flex items-center gap-3">
                      <AlertCircle className="w-3.5 h-3.5 text-red-500 shrink-0" />
                      <span className="font-mono text-xs truncate flex-1">{b.email}</span>
                      {b.bounce_count > 1 && (
                        <span className="text-[10px] font-mono uppercase tracking-wider text-red-600 bg-red-50 border border-red-200 px-1.5 py-0.5 rounded shrink-0">
                          {b.bounce_count}× bounced
                        </span>
                      )}
                      <span className="text-[11px] text-slate-400 font-mono shrink-0">
                        {b.last_bounced_at ? formatDate(b.last_bounced_at) : ""}
                      </span>
                    </li>
                  ))}
                </ul>
              </>
            ) : null}
          </div>
        )}
      </div>
      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        <button
          type="button"
          onClick={() => {
            const next = !showUnsubs;
            setShowUnsubs(next);
            if (next && !unsubs) loadUnsubs();
          }}
          className="w-full px-4 py-3 border-b border-slate-100 bg-slate-50 hover:bg-slate-100 flex items-center justify-between text-left transition"
          data-testid="marketing-toggle-unsubs"
        >
          <div className="text-[11px] uppercase tracking-[0.12em] font-mono text-slate-600">
            Unsubscribes {unsubs !== null && <span className="text-slate-900 ml-1">· {unsubs.length}</span>}
          </div>
          <span className="text-xs text-slate-400 font-mono">{showUnsubs ? "hide ▲" : "show ▼"}</span>
        </button>
        {showUnsubs && (
          <div>
            {unsubsLoading ? (
              <div className="p-6 text-center text-sm text-slate-500 flex items-center justify-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading…
              </div>
            ) : unsubs && unsubs.length === 0 ? (
              <div className="p-8 text-center text-sm text-slate-500">
                <Ban className="w-6 h-6 text-slate-300 mx-auto mb-2" />
                No one has unsubscribed yet — nice.
              </div>
            ) : unsubs && unsubs.length > 0 ? (
              <>
                <div className="px-4 py-2 border-b border-slate-100 flex items-center justify-between text-xs">
                  <div className="text-slate-500">
                    These emails are auto-skipped on every future marketing send from this agency.
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={exportUnsubsCsv}
                    data-testid="marketing-unsubs-export-btn"
                  >
                    Export CSV
                  </Button>
                </div>
                <ul className="divide-y divide-slate-100 max-h-80 overflow-y-auto">
                  {unsubs.map((u) => (
                    <li key={u.email} className="px-4 py-2 text-sm flex items-center gap-3">
                      <Ban className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                      <span className="font-mono text-xs truncate flex-1">{u.email}</span>
                      {u.reason === "spam_complaint" && (
                        <span className="text-[10px] font-mono uppercase tracking-wider text-red-600 bg-red-50 border border-red-200 px-1.5 py-0.5 rounded shrink-0">
                          spam complaint
                        </span>
                      )}
                      <span className="text-[11px] text-slate-400 font-mono shrink-0">
                        {u.unsubscribed_at ? formatDate(u.unsubscribed_at) : ""}
                      </span>
                    </li>
                  ))}
                </ul>
              </>
            ) : null}
          </div>
        )}
      </div>

      <CampaignDetailDialog
        campaign={detailCampaign}
        onOpenChange={(v) => !v && setDetailCampaign(null)}
        onRefresh={loadCampaigns}
      />

      <DuplicateRecipientsDialog
        campaign={duplicateSource}
        onOpenChange={(v) => !v && setDuplicateSource(null)}
        onDone={() => {
          setDuplicateSource(null);
          loadCampaigns();
        }}
      />

      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this campaign?</AlertDialogTitle>
            <AlertDialogDescription>
              Removes “{confirmDelete?.subject}” and all its recipient log entries. Cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="campaign-confirm-delete-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Composer dialog
// ---------------------------------------------------------------------------
function ComposerDialog({ open, onOpenChange, agencyId, agencyMeta, onCreated }) {
  const [step, setStep] = useState(1);
  const [prompt, setPrompt] = useState("");
  const [pdf, setPdf] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [subject, setSubject] = useState("");
  const [bodyText, setBodyText] = useState("");
  const [csvPreview, setCsvPreview] = useState(null);
  const [csvName, setCsvName] = useState("");
  const [scheduledAt, setScheduledAt] = useState("");
  const [sendChoice, setSendChoice] = useState("now"); // "now" | "draft" | "schedule"
  const [saving, setSaving] = useState(false);
  // Fresh copy of the agency doc — refetched on every open so a signature
  // added moments ago on the Agencies page shows up here without a full reload.
  const [freshAgency, setFreshAgency] = useState(agencyMeta);
  const pdfRef = useRef();
  const csvRef = useRef();

  useEffect(() => {
    if (open) {
      setStep(1);
      setPrompt("");
      setPdf(null);
      setSubject("");
      setBodyText("");
      setCsvPreview(null);
      setCsvName("");
      setScheduledAt("");
      setSendChoice("now");
      // Snap in the parent's copy first, then hit the network so we
      // always render the freshest signature/reply-to.
      setFreshAgency(agencyMeta);
      if (agencyId) {
        api
          .get(`/rep-agencies/${agencyId}`)
          .then(({ data }) => setFreshAgency(data))
          .catch(() => {});
      }
    }
    // NOTE: dependency is ONLY `open` — do not add agencyMeta or agencyId
    // here or the parent's polling will wipe the form every 6 seconds.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const activeAgency = freshAgency || agencyMeta;

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast.error("Tell the AI what to write about first.");
      return;
    }
    setGenerating(true);
    try {
      const form = new FormData();
      form.append("prompt", prompt);
      if (pdf) form.append("spec_sheet", pdf);
      const { data } = await api.post("/marketing/generate", form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90_000,
      });
      setSubject(data.subject);
      setBodyText(data.body_text);
      setStep(2);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGenerating(false);
    }
  };

  const handleCsv = async (file) => {
    if (!file) return;
    setCsvName(file.name);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/marketing/csv-preview", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setCsvPreview(data);
    } catch (e) {
      toast.error(formatApiError(e));
      setCsvPreview(null);
      setCsvName("");
    }
  };

  const canSubmit = subject.trim() && bodyText.trim() && csvPreview && csvPreview.valid_count > 0;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      const payload = {
        subject: subject.trim(),
        body_text: bodyText.trim(),
        recipients: csvPreview.recipients,
        agency_id: agencyId,
      };
      if (sendChoice === "schedule" && scheduledAt) {
        payload.scheduled_at = new Date(scheduledAt).toISOString();
      }
      const { data: campaign } = await api.post("/marketing", payload);

      // If a spec sheet PDF was uploaded for AI drafting, also attach it to
      // the outgoing emails. Independent from the draft-generation call.
      if (pdf) {
        try {
          const specForm = new FormData();
          specForm.append("spec_sheet", pdf);
          await api.post(`/marketing/${campaign.id}/spec-sheet`, specForm, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 60_000,
          });
        } catch (attachErr) {
          toast.warning(
            `Campaign saved, but couldn't attach the PDF: ${formatApiError(attachErr)}. You can retry from the campaign detail.`
          );
        }
      }

      if (sendChoice === "now") {
        await api.post(`/marketing/${campaign.id}/send`);
        toast.success(`Sending to ${csvPreview.valid_count} recipients — 1 email / 3 seconds`);
      } else if (sendChoice === "schedule") {
        toast.success(`Scheduled for ${formatDate(payload.scheduled_at)}`);
      } else {
        toast.success("Draft saved");
      }
      onCreated();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="marketing-composer">
        <DialogHeader>
          <DialogTitle>New marketing campaign</DialogTitle>
          <DialogDescription>
            Draft with AI, review, upload your recipient list, and send.
          </DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-4">
            <div>
              <Label htmlFor="prompt-input" className="text-sm">
                What should the AI write about?
              </Label>
              <Textarea
                id="prompt-input"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Example: Announce the new TCP DT-Series 2x4 flat panel. Highlight energy savings, DLC-listed status, and the 5-year warranty. Aim for lighting distributors placing bids next quarter."
                className="min-h-[120px] mt-1"
                data-testid="marketing-prompt-input"
              />
            </div>

            <div>
              <Label className="text-sm">Optional: attach a spec sheet PDF</Label>
              <div className="mt-1 flex items-center gap-2">
                <input
                  ref={pdfRef}
                  type="file"
                  accept="application/pdf"
                  onChange={(e) => setPdf(e.target.files?.[0] || null)}
                  className="hidden"
                  data-testid="marketing-pdf-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => pdfRef.current?.click()}
                  data-testid="marketing-pdf-pick-btn"
                >
                  <FileUp className="w-4 h-4 mr-2" />
                  {pdf ? "Change PDF" : "Choose PDF"}
                </Button>
                {pdf && (
                  <span className="text-xs text-slate-500 truncate flex-1">{pdf.name}</span>
                )}
                {pdf && (
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    onClick={() => {
                      setPdf(null);
                      if (pdfRef.current) pdfRef.current.value = "";
                    }}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>
              <p className="text-[11px] text-slate-400 mt-1">
                The AI reads the PDF and cites only real features from it — and the same PDF is attached to every outgoing email so recipients can download the full spec sheet. Skip if you just want a plain prompt-only draft.
              </p>
            </div>

            <DialogFooter>
              <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button
                onClick={handleGenerate}
                disabled={generating || !prompt.trim()}
                data-testid="marketing-generate-btn"
              >
                {generating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Drafting…
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" /> Draft with AI
                  </>
                )}
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <div>
              <Label htmlFor="subj-input" className="text-sm">Subject</Label>
              <Input
                id="subj-input"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                maxLength={200}
                data-testid="marketing-subject-input"
              />
            </div>
            <div>
              <Label htmlFor="body-input" className="text-sm">
                Email body{" "}
                <span className="text-[11px] text-slate-400">
                  — use <code>{"{{name}}"}</code> to personalize with the recipient&apos;s first name
                </span>
              </Label>
              <Textarea
                id="body-input"
                value={bodyText}
                onChange={(e) => setBodyText(e.target.value)}
                className="min-h-[220px] mt-1 font-mono text-[13px]"
                data-testid="marketing-body-input"
              />
              <p className="text-[11px] text-slate-400 mt-1">
                The platform automatically adds the unsubscribe footer — don&apos;t paste one in yourself.
              </p>
            </div>

            {activeAgency?.marketing_signature ? (
              <div>
                <div className="text-[10px] uppercase tracking-[0.14em] font-mono text-slate-500 mb-1">
                  Signature — auto-appended below your email
                </div>
                <div
                  className="rounded-md border border-slate-200 bg-slate-50 p-3 text-[13px] text-slate-700 whitespace-pre-wrap leading-relaxed border-t-2 border-t-slate-300"
                  data-testid="marketing-signature-preview"
                >
                  {activeAgency.marketing_signature}
                </div>
                <p className="text-[11px] text-slate-400 mt-1">
                  Set on the{" "}
                  <a href="/agencies" className="underline hover:text-slate-600">
                    Agencies page
                  </a>{" "}
                  — edit the agency to change it.
                </p>
              </div>
            ) : (
              <div
                className="rounded-md border border-amber-200 bg-amber-50 p-3 text-[13px] text-amber-900 flex items-start gap-2"
                data-testid="marketing-signature-missing"
              >
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                <div className="flex-1 leading-relaxed">
                  <strong>No signature set for this agency.</strong> Add one on the{" "}
                  <a href="/agencies" className="underline font-semibold hover:text-amber-950">
                    Agencies page
                  </a>{" "}
                  — otherwise your email will end abruptly at the body.
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="ghost" onClick={() => setStep(1)}>← Back</Button>
              <Button
                onClick={() => setStep(3)}
                disabled={!subject.trim() || !bodyText.trim()}
                data-testid="marketing-approve-draft-btn"
              >
                Looks good → recipients
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
            <div>
              <Label className="text-sm">Recipient CSV</Label>
              <p className="text-[11px] text-slate-500 mt-1 mb-2">
                Required columns: <code>email</code>, <code>name</code>. Extra columns are ignored. Duplicates and invalid emails will be skipped.
              </p>
              <input
                ref={csvRef}
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => handleCsv(e.target.files?.[0])}
                className="hidden"
                data-testid="marketing-csv-input"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => csvRef.current?.click()}
                data-testid="marketing-csv-pick-btn"
              >
                <FileUp className="w-4 h-4 mr-2" />
                {csvPreview ? "Change CSV" : "Choose CSV"}
              </Button>
              {csvName && (
                <div className="mt-2 text-xs text-slate-500">{csvName}</div>
              )}
              {csvPreview && (
                <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm space-y-2">
                  <div className="flex items-center gap-2 text-emerald-700">
                    <CheckCircle2 className="w-4 h-4" />
                    <span className="font-semibold">{csvPreview.valid_count}</span> valid recipients ready
                  </div>
                  {csvPreview.invalid_count > 0 && (
                    <details className="text-amber-800" data-testid="marketing-csv-skipped-details">
                      <summary className="flex items-center gap-2 cursor-pointer select-none hover:text-amber-900">
                        <AlertCircle className="w-4 h-4" />
                        <span className="font-semibold">{csvPreview.invalid_count}</span> row(s) skipped
                        <span className="text-[11px] font-mono text-amber-600 ml-1">— click for details</span>
                      </summary>
                      <div className="mt-2 pl-6 space-y-2">
                        {csvPreview.invalid_reasons?.length > 0 && (
                          <ul className="text-xs space-y-0.5">
                            {csvPreview.invalid_reasons.map((r) => (
                              <li key={r.reason} className="flex justify-between gap-3 border-b border-amber-100 py-0.5">
                                <span className="capitalize">{r.reason}</span>
                                <span className="font-mono font-semibold">{r.count}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                        {csvPreview.invalid_sample?.length > 0 && (
                          <div>
                            <div className="text-[11px] uppercase tracking-[0.1em] font-mono text-amber-700 mb-1">
                              First {Math.min(csvPreview.invalid_sample.length, 10)} skipped rows
                            </div>
                            <ul className="text-[11px] font-mono space-y-0.5 max-h-40 overflow-y-auto">
                              {csvPreview.invalid_sample.slice(0, 10).map((r) => (
                                <li key={r.row} className="flex gap-2">
                                  <span className="text-amber-500 shrink-0">Row {r.row}:</span>
                                  <span className="text-amber-900 break-all">{r.reason}</span>
                                </li>
                              ))}
                            </ul>
                            {csvPreview.invalid_count > 10 && (
                              <div className="text-[10px] text-amber-600 mt-1 italic">
                                +{csvPreview.invalid_count - 10} more — pattern is likely the same
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </details>
                  )}
                  {csvPreview.sample?.length > 0 && (
                    <div className="text-xs text-slate-500 mt-2 font-mono">
                      Sample: {csvPreview.sample.map((r) => r.email).join(", ")}
                      {csvPreview.valid_count > csvPreview.sample.length && "…"}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div>
              <Label className="text-sm">When should we send?</Label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-2">
                {[
                  { value: "now", label: "Send now", icon: Send },
                  { value: "schedule", label: "Schedule", icon: Clock },
                  { value: "draft", label: "Save draft", icon: Ban },
                ].map((opt) => {
                  const Icon = opt.icon;
                  const active = sendChoice === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setSendChoice(opt.value)}
                      className={`px-3 py-2 border rounded-md text-sm flex items-center justify-center gap-2 transition ${
                        active
                          ? "border-slate-900 bg-slate-900 text-white"
                          : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
                      }`}
                      data-testid={`marketing-send-choice-${opt.value}`}
                    >
                      <Icon className="w-4 h-4" /> {opt.label}
                    </button>
                  );
                })}
              </div>
              {sendChoice === "schedule" && (
                <div className="mt-3">
                  <Label htmlFor="sched-input" className="text-xs">Deliver at (your local time)</Label>
                  <Input
                    id="sched-input"
                    type="datetime-local"
                    value={scheduledAt}
                    onChange={(e) => setScheduledAt(e.target.value)}
                    data-testid="marketing-scheduled-at"
                    className="mt-1"
                  />
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="ghost" onClick={() => setStep(2)}>← Back</Button>
              <Button
                onClick={handleSubmit}
                disabled={
                  saving ||
                  !canSubmit ||
                  (sendChoice === "schedule" && !scheduledAt)
                }
                data-testid="marketing-submit-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…
                  </>
                ) : sendChoice === "now" ? (
                  <>
                    <Send className="w-4 h-4 mr-2" /> Send campaign
                  </>
                ) : sendChoice === "schedule" ? (
                  <>
                    <Clock className="w-4 h-4 mr-2" /> Schedule
                  </>
                ) : (
                  "Save draft"
                )}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Campaign detail dialog (view body, recipient log)
// ---------------------------------------------------------------------------
function CampaignDetailDialog({ campaign, onOpenChange, onRefresh }) {
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    if (!campaign) return;
    setLoading(true);
    const params = statusFilter === "all" ? {} : { status: statusFilter };
    api
      .get(`/marketing/${campaign.id}/recipients`, { params })
      .then(({ data }) => setRecipients(data || []))
      .catch(() => setRecipients([]))
      .finally(() => setLoading(false));
  }, [campaign, statusFilter]);

  const counts = useMemo(() => {
    if (!campaign) return {};
    return {
      total: campaign.recipient_count,
      sent: campaign.sent_count,
      failed: campaign.failed_count,
      unsub: campaign.skipped_unsub_count,
    };
  }, [campaign]);

  const [retrying, setRetrying] = useState(false);
  const handleRetryFailed = async () => {
    if (!campaign || retrying) return;
    setRetrying(true);
    try {
      const { data } = await api.post(`/marketing/${campaign.id}/retry-failed`);
      toast.success(`Re-queued ${data.requeued} failed recipient${data.requeued === 1 ? "" : "s"} — sending now`);
      onRefresh?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRetrying(false);
    }
  };

  if (!campaign) return null;

  return (
    <Dialog open={!!campaign} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-2 flex-wrap">
            <StatusBadge status={campaign.status} />
            <DialogTitle className="text-lg">{campaign.subject}</DialogTitle>
          </div>
          <DialogDescription>
            Created {formatDate(campaign.created_at)} by {campaign.created_by_email}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard label="Recipients" value={counts.total} />
            <StatCard label="Sent" value={counts.sent} tone="emerald" />
            <StatCard label="Failed" value={counts.failed} tone={counts.failed ? "red" : "slate"} />
            <StatCard label="Unsub" value={counts.unsub} tone={counts.unsub ? "amber" : "slate"} />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard
              label="Delivered"
              value={campaign.delivered_count || 0}
              rate={counts.sent ? Math.round(((campaign.delivered_count || 0) / counts.sent) * 100) : null}
              tone="slate"
            />
            <StatCard
              label="Opens"
              value={campaign.opened_count || 0}
              rate={counts.sent ? Math.round(((campaign.opened_count || 0) / counts.sent) * 100) : null}
              tone={campaign.opened_count ? "sky" : "slate"}
            />
            <StatCard
              label="Clicks"
              value={campaign.clicked_count || 0}
              rate={counts.sent ? Math.round(((campaign.clicked_count || 0) / counts.sent) * 100) : null}
              tone={campaign.clicked_count ? "sky" : "slate"}
            />
            <StatCard
              label="Bounced"
              value={campaign.bounced_count || 0}
              tone={campaign.bounced_count ? "red" : "slate"}
            />
          </div>

          <div>
            <div className="text-[11px] uppercase tracking-[0.12em] font-mono text-slate-500 mb-1">
              Body preview
            </div>
            <div className="rounded-md border border-slate-200 bg-white p-4 text-sm whitespace-pre-wrap max-h-56 overflow-y-auto">
              {campaign.body_text}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] uppercase tracking-[0.12em] font-mono text-slate-500">
                Recipients log
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="h-8 w-40 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="queued">Queued</SelectItem>
                  <SelectItem value="sent">Sent</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                  <SelectItem value="unsubscribed">Unsubscribed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {loading ? (
              <div className="p-6 text-center text-sm text-slate-500 flex items-center justify-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading…
              </div>
            ) : recipients.length === 0 ? (
              <div className="p-6 text-center text-sm text-slate-500">No rows.</div>
            ) : (
              <ul className="divide-y divide-slate-100 border border-slate-200 rounded-md max-h-64 overflow-y-auto">
                {recipients.map((r) => (
                  <li key={r.id} className="px-3 py-2 text-sm flex items-center gap-3">
                    <RecipientStatusIcon status={r.status} />
                    <span className="font-mono text-xs truncate flex-1">{r.email}</span>
                    <span className="text-xs text-slate-500 truncate hidden md:inline w-24">
                      {r.name || "—"}
                    </span>
                    <span className="text-[10px] font-mono shrink-0 w-20 text-right">
                      {r.opens_count ? (
                        <span className="text-sky-700">{r.opens_count} open{r.opens_count > 1 ? "s" : ""}</span>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </span>
                    <span className="text-[10px] font-mono shrink-0 w-20 text-right">
                      {r.clicks_count ? (
                        <span className="text-sky-700">{r.clicks_count} click{r.clicks_count > 1 ? "s" : ""}</span>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </span>
                    <span className="text-[11px] text-slate-400 shrink-0 font-mono w-32 text-right">
                      {r.sent_at ? formatDate(r.sent_at) : ""}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <DialogFooter>
          {campaign.failed_count > 0 && campaign.status !== "sending" && (
            <Button
              variant="outline"
              onClick={handleRetryFailed}
              disabled={retrying}
              className="border-amber-300 text-amber-800 hover:bg-amber-50"
              data-testid="marketing-retry-failed-btn"
            >
              {retrying ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Re-queuing…
                </>
              ) : (
                <>
                  <Play className="w-4 h-4 mr-2" />
                  Retry {campaign.failed_count} failed
                </>
              )}
            </Button>
          )}
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Duplicate recipient dialog — used after clicking Duplicate on a campaign.
// The new draft already has subject/body/PDF copied; user just needs a fresh
// CSV. This is a slimmed-down step 3 of the composer, scoped to one draft.
// ---------------------------------------------------------------------------
function DuplicateRecipientsDialog({ campaign, onOpenChange, onDone }) {
  const [csvPreview, setCsvPreview] = useState(null);
  const [csvName, setCsvName] = useState("");
  const [sendChoice, setSendChoice] = useState("now");
  const [scheduledAt, setScheduledAt] = useState("");
  const [saving, setSaving] = useState(false);
  const csvRef = useRef();

  useEffect(() => {
    if (campaign) {
      setCsvPreview(null);
      setCsvName("");
      setSendChoice("now");
      setScheduledAt("");
    }
  }, [campaign?.id]);

  const handleCsv = async (file) => {
    if (!file) return;
    setCsvName(file.name);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/marketing/csv-preview", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setCsvPreview(data);
    } catch (e) {
      toast.error(formatApiError(e));
      setCsvPreview(null);
      setCsvName("");
    }
  };

  const canSubmit = csvPreview && csvPreview.valid_count > 0;

  const handleSubmit = async () => {
    if (!canSubmit || !campaign) return;
    setSaving(true);
    try {
      const patch = { recipients: csvPreview.recipients };
      if (sendChoice === "schedule" && scheduledAt) {
        patch.scheduled_at = new Date(scheduledAt).toISOString();
      }
      await api.patch(`/marketing/${campaign.id}`, patch);
      if (sendChoice === "now") {
        await api.post(`/marketing/${campaign.id}/send`);
        toast.success(`Sending to ${csvPreview.valid_count} recipients — 1 email / 3 seconds`);
      } else if (sendChoice === "schedule") {
        toast.success(`Scheduled for ${formatDate(patch.scheduled_at)}`);
      } else {
        toast.success("Draft saved — you can send later");
      }
      onDone();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (!campaign) return null;

  return (
    <Dialog open={!!campaign} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="marketing-duplicate-dialog">
        <DialogHeader>
          <DialogTitle>New batch — same email, new recipients</DialogTitle>
          <DialogDescription>
            Reusing the subject, body{campaign.has_spec_sheet ? ", and PDF attachment" : ""} from
            &ldquo;{campaign.subject}&rdquo;. Upload the next CSV batch and send.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600 space-y-1">
            <div>
              <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-slate-500">
                Subject:
              </span>{" "}
              <span className="font-semibold text-slate-900">{campaign.subject}</span>
            </div>
            {campaign.has_spec_sheet && (
              <div className="flex items-center gap-1 text-slate-600">
                <FileUp className="w-3 h-3" /> {campaign.spec_sheet_filename || "PDF attached"}
              </div>
            )}
          </div>

          <div>
            <Label className="text-sm">New recipient CSV</Label>
            <p className="text-[11px] text-slate-500 mt-1 mb-2">
              Required columns: <code>email</code>, <code>name</code>. Duplicates and invalid emails will be skipped.
            </p>
            <input
              ref={csvRef}
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => handleCsv(e.target.files?.[0])}
              className="hidden"
              data-testid="duplicate-csv-input"
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => csvRef.current?.click()}
              data-testid="duplicate-csv-pick-btn"
            >
              <FileUp className="w-4 h-4 mr-2" />
              {csvPreview ? "Change CSV" : "Choose CSV"}
            </Button>
            {csvName && <div className="mt-2 text-xs text-slate-500">{csvName}</div>}
            {csvPreview && (
              <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm space-y-1">
                <div className="flex items-center gap-2 text-emerald-700">
                  <CheckCircle2 className="w-4 h-4" />
                  <span className="font-semibold">{csvPreview.valid_count}</span> valid recipients ready
                </div>
                {csvPreview.invalid_count > 0 && (
                  <div className="flex items-center gap-2 text-amber-700 text-xs">
                    <AlertCircle className="w-4 h-4" />
                    {csvPreview.invalid_count} row(s) skipped
                  </div>
                )}
              </div>
            )}
          </div>

          <div>
            <Label className="text-sm">When?</Label>
            <div className="grid grid-cols-3 gap-2 mt-2">
              {[
                { value: "now", label: "Send now", icon: Send },
                { value: "schedule", label: "Schedule", icon: Clock },
                { value: "draft", label: "Save draft", icon: Ban },
              ].map((opt) => {
                const Icon = opt.icon;
                const active = sendChoice === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setSendChoice(opt.value)}
                    className={`px-3 py-2 border rounded-md text-sm flex items-center justify-center gap-2 transition ${
                      active
                        ? "border-slate-900 bg-slate-900 text-white"
                        : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
                    }`}
                    data-testid={`duplicate-send-choice-${opt.value}`}
                  >
                    <Icon className="w-4 h-4" /> {opt.label}
                  </button>
                );
              })}
            </div>
            {sendChoice === "schedule" && (
              <div className="mt-3">
                <Label htmlFor="dup-sched" className="text-xs">
                  Deliver at (your local time)
                </Label>
                <Input
                  id="dup-sched"
                  type="datetime-local"
                  value={scheduledAt}
                  onChange={(e) => setScheduledAt(e.target.value)}
                  className="mt-1"
                  data-testid="duplicate-scheduled-at"
                />
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={
              saving ||
              !canSubmit ||
              (sendChoice === "schedule" && !scheduledAt)
            }
            data-testid="duplicate-submit-btn"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…
              </>
            ) : sendChoice === "now" ? (
              <>
                <Send className="w-4 h-4 mr-2" /> Send batch
              </>
            ) : sendChoice === "schedule" ? (
              <>
                <Clock className="w-4 h-4 mr-2" /> Schedule
              </>
            ) : (
              "Save draft"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function StatCard({ label, value, tone = "slate", rate = null }) {
  const toneCls = {
    slate: "bg-slate-50 text-slate-900 border-slate-200",
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
    red: "bg-red-50 text-red-800 border-red-200",
    amber: "bg-amber-50 text-amber-800 border-amber-200",
    sky: "bg-sky-50 text-sky-800 border-sky-200",
  }[tone];
  return (
    <div className={`rounded-md border ${toneCls} p-3`}>
      <div className="text-[10px] uppercase tracking-[0.14em] font-mono text-slate-500">
        {label}
      </div>
      <div className="flex items-baseline gap-2 mt-1">
        <div className="text-2xl font-semibold">{value ?? 0}</div>
        {rate !== null && (
          <div className="text-xs font-mono text-slate-500">{rate}%</div>
        )}
      </div>
    </div>
  );
}

function RecipientStatusIcon({ status }) {
  if (status === "sent") return <CheckCircle2 className="w-4 h-4 text-emerald-600" />;
  if (status === "failed") return <AlertCircle className="w-4 h-4 text-red-600" />;
  if (status === "unsubscribed") return <Ban className="w-4 h-4 text-amber-600" />;
  return <Clock className="w-4 h-4 text-slate-400" />;
}
